import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ArrowBack } from '@mui/icons-material'
import { Box, Button, ButtonBase, Dialog, DialogContent, Grid, Stack, Typography } from '@mui/material'

import GRDialogTitle from '../../../../../components/GRDialogTitle/GRDialogTitle'
import { InternalGameScreenshot, useGetInternalGameScreenshotsQuery } from '../../../../api/screenshot-links'
import { QueryStateBoundary } from '../../../../components/QueryStateBoundary/QueryStateBoundary'
import { BulkAssignFeature, BulkAssignGame } from '../../types/BulkAssign'
import { getFeatureScreenshots } from '../helpers/bulkAssignHelpers'

type FeatureScreenshotsDialogProps = {
  target?: { game: BulkAssignGame; feature: BulkAssignFeature }
  onClose: () => void
}

export const FeatureScreenshotsDialog: FC<FeatureScreenshotsDialogProps> = ({ target, onClose }) => {
  const { t } = useTranslation()
  const [selected, setSelected] = useState<InternalGameScreenshot>()
  const screenshotsQuery = useGetInternalGameScreenshotsQuery(target?.game.id ?? '', { skip: !target })
  const screenshots = useMemo(() => (target ? getFeatureScreenshots(screenshotsQuery.data, target.feature.legacyId) : []), [screenshotsQuery.data, target])

  useEffect(() => {
    if (!target) setSelected(undefined)
  }, [target])

  const version = (screenshot: InternalGameScreenshot) => t('internal-analysts:bulk_assign_screenshot_version', { version: screenshot.gameVersion || '---' })

  return (
    <Dialog open={!!target} onClose={onClose} fullWidth maxWidth="lg">
      <GRDialogTitle onClose={onClose}>
        <Box minWidth={0}>
          {t('internal-analysts:bulk_assign_screenshots_title', { game: target?.game.name ?? '' })}
          <Typography variant="body2" color="text.secondary">
            {t('internal-analysts:bulk_assign_screenshots_feature', { name: target?.feature.name ?? '' })}
          </Typography>
        </Box>
      </GRDialogTitle>
      <DialogContent>
        <QueryStateBoundary
          isLoading={screenshotsQuery.isFetching}
          isError={screenshotsQuery.isError}
          errorText={t('internal-analysts:bulk_assign_screenshots_load_failed')}
          skeletonHeight={240}
        >
          {!screenshots.length ? (
            <Typography color="text.secondary">{t('internal-analysts:bulk_assign_screenshots_none')}</Typography>
          ) : selected ? (
            <Stack spacing={2} alignItems="center">
              <Box component="img" src={selected.url} alt={version(selected)} sx={{ maxWidth: '100%', maxHeight: '70vh' }} />
              <Typography variant="body2">{version(selected)}</Typography>
              <Button startIcon={<ArrowBack />} onClick={() => setSelected(undefined)}>
                {t('internal-common:back')}
              </Button>
            </Stack>
          ) : (
            <Grid container spacing={2}>
              {screenshots.map((screenshot, index) => (
                <Grid item xs={6} sm={4} md={3} key={screenshot.id}>
                  <ButtonBase
                    aria-label={t('internal-analysts:bulk_assign_screenshot', { index: index + 1 })}
                    onClick={() => setSelected(screenshot)}
                    sx={{ width: '100%', display: 'block' }}
                  >
                    <Box component="img" src={screenshot.url} alt="" loading="lazy" sx={{ width: '100%', display: 'block' }} />
                  </ButtonBase>
                  <Typography variant="caption" color="text.secondary">
                    {version(screenshot)}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          )}
        </QueryStateBoundary>
      </DialogContent>
    </Dialog>
  )
}
